import axios from 'axios';
import { API_URL, API_GATEWAY_URL, SERVICE_NAME, PUBLIC_API_TOKEN } from '../config';
import authService from './auth';
import refreshTokenManager from './refreshTokenManager';

// Shared manager координирует refresh между auth.js и apiService.js

// Создаем экземпляр axios для основного API
const mainApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Создаем экземпляр axios для API Gateway
const gatewayApi = axios.create({
  baseURL: API_GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
});

// Создаем экземпляр axios для публичных запросов через API Gateway (без Authorization)
const publicApi = axios.create({
  baseURL: API_GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Token': PUBLIC_API_TOKEN
  },
  withCredentials: false,
});

// Общая функция для добавления токена к запросам
const addTokenToRequest = (config, apiName) => {
  const token = localStorage.getItem('token');
  console.log(`🚀 ${apiName} API Request:`, {
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`,
    token: token ? 'Present' : 'Missing'
  });

  // Добавляем домен клиента в заголовки запроса
  const clientDomain = window.location.origin;
  config.headers['X-Client-Domain'] = clientDomain;

  // Добавляем токен, если он есть (валидация токена происходит на API Gateway)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

// Общая функция для обработки ответов
const handleResponse = (response, apiName) => {
  console.log(`✅ ${apiName} API Response:`, {
    status: response.status,
    url: response.config.url,
    data: response.data
  });
  return response;
};

// Общая функция для обработки ошибок
const handleError = async (error, apiName) => {
  const originalRequest = error.config;

  console.log(`❌ ${apiName} API Error:`, {
    status: error.response?.status,
    url: originalRequest?.url,
    message: error.message,
    data: error.response?.data
  });

  // Обработка 401 ошибки (токен невалиден или истек)
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    // Если уже идет процесс обновления токена, используем существующий promise
    if (refreshTokenManager.isCurrentlyRefreshing()) {
      const existingPromise = refreshTokenManager.getRefreshPromise();
      if (existingPromise) {
        try {
          const token = await existingPromise;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      // Fallback: добавляем в очередь если promise не найден
      return new Promise((resolve, reject) => {
        refreshTokenManager.addToQueue(resolve, reject);
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    // Создаем shared promise для refresh операции
    const refreshPromise = (async () => {
      try {
        // Обновляем токен через auth service
        const response = await authService.refreshToken();
        const newToken = response?.tokens?.accessToken || response?.token;

        if (newToken) {
          // Обрабатываем очередь запросов с новым токеном
          refreshTokenManager.processQueue(null, newToken);
          return newToken;
        } else {
          throw new Error('No token received from refresh');
        }
      } catch (refreshError) {
        // Logout только если рефреш явно вернул 401 (невалидный refresh token)
        // При сетевых ошибках, 5xx, таймауте — не делаем logout
        if (refreshError?.response?.status === 401) {
          // Очищаем токен из localStorage
          localStorage.removeItem('token');

          // Пытаемся очистить сессию на бекенде (fire-and-forget)
          authService.logout().catch(() => {
            // Ошибка ожидаема - refresh token уже невалидный
          });

          // Уведомляем приложение о необходимости logout через событие
          window.dispatchEvent(new CustomEvent('auth:logout', {
            detail: {
              reason: 'refresh_token_expired',
              timestamp: new Date().toISOString()
            }
          }));
        }

        // Отклоняем все запросы в очереди
        refreshTokenManager.processQueue(refreshError, null);

        throw refreshError;
      } finally {
        refreshTokenManager.setRefreshing(false);
      }
    })();

    // Сохраняем promise и флаг ДО начала async операции,
    // чтобы параллельные запросы сразу получали существующий promise
    refreshTokenManager.setRefreshPromise(refreshPromise);
    refreshTokenManager.setRefreshing(true);

    try {
      const newToken = await refreshPromise;
      // Повторяем оригинальный запрос с новым токеном
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }

  return Promise.reject(error);
};

// Настройка интерцепторов для основного API
mainApi.interceptors.request.use((config) => addTokenToRequest(config, 'Main'));
mainApi.interceptors.response.use(
  (response) => handleResponse(response, 'Main'),
  (error) => handleError(error, 'Main')
);

// Настройка интерцепторов для Gateway API
gatewayApi.interceptors.request.use((config) => addTokenToRequest(config, 'Gateway'));
gatewayApi.interceptors.response.use(
  (response) => handleResponse(response, 'Gateway'),
  (error) => handleError(error, 'Gateway')
);

// ==================== TALENT METHODS ====================

const talentService = {
  // Получить всех талантов с фильтрацией и пагинацией
  getTalents: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talents', { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching talents:', error);
      throw error;
    }
  },

  // Получить таланта по ID
  getTalentById: async (id) => {
    try {
      const response = await mainApi.get(`/talents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching talent:', error);
      throw error;
    }
  },

  
  // Получить таланта по ID
  getTalentByIdWithRelations: async (id) => {
    try {
      const response = await gatewayApi.get(`/talents/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching talent:', error);
      throw error;
    }
  },

  // Получить несколько талантов по массиву ID (для экспорта, один запрос)
  getTalentsByIdsForExport: async (ids) => {
    try {
      const response = await gatewayApi.get(`/talents/export?ids=${ids.join(',')}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching talents for export:', error);
      throw error;
    }
  },

  // Создать нового таланта
  createTalent: async (talentData) => {
    try {
      const response = await mainApi.post('/talents', talentData);
      return response.data;
    } catch (error) {
      console.error('Error creating talent:', error);
      throw error;
    }
  },

  // Обновить таланта
  updateTalent: async (id, talentData) => {
    try {
      const response = await mainApi.put(`/talents/${id}`, talentData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent:', error);
      throw error;
    }
  },

  updateTalentContacts: async (talentId, contacts) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/contacts`, { contacts });
      return response.data;
    } catch (error) {
      console.error('Error updating talent contacts:', error);
      throw error;
    }
  },

  addTalentContact: async (talentId, contactData) => {
    try {
      const response = await mainApi.post(`/talents/${talentId}/contacts`, contactData);
      return response.data;
    } catch (error) {
      console.error('Error adding talent contact:', error);
      throw error;
    }
  },

  updateTalentContact: async (talentId, contactId, contactData) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/contacts/${contactId}`, contactData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent contact:', error);
      throw error;
    }
  },

  deleteTalentContact: async (talentId, contactId) => {
    try {
      const response = await mainApi.delete(`/talents/${talentId}/contacts/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent contact:', error);
      throw error;
    }
  },

  // Обновити або створити зарплату таланта
  updateTalentSalary: async (talentId, salaryData) => {
    try {
      const response = await mainApi.put(`/talents/${talentId}/salary`, salaryData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent salary:', error);
      throw error;
    }
  },

  // Получить зарплату таланта
  getTalentSalary: async (talentId) => {
    try {
      const response = await mainApi.get(`/talents/${talentId}/salary`);
      return response.data;
    } catch (error) {
      console.error('Error fetching talent salary:', error);
      throw error;
    }
  },

  // Получить публичный профиль таланта
  getPublicProfile: async (talentId) => {
    try {
      const response = await mainApi.get(`/talents/${talentId}/public-profile`);
      return response.data;
    } catch (error) {
      console.error('Error fetching public profile:', error);
      throw error;
    }
  },

  // Обновить или создать публичный профиль таланта
  upsertPublicProfile: async (talentId, data) => {
    try {
      const response = await mainApi.put(`/talents/${talentId}/public-profile`, data);
      return response.data;
    } catch (error) {
      console.error('Error upserting public profile:', error);
      throw error;
    }
  },

  // Удалить таланта
  deleteTalent: async (id) => {
    try {
      const response = await mainApi.delete(`/talents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent:', error);
      throw error;
    }
  },

  // Объединение дубликата: keepId остаётся, sourceId удаляется.
  // dryRun прогоняет всю транзакцию и откатывает её — используется для предпросмотра.
  // confirmToken из предпросмотра: если данные изменились после него, бэкенд вернёт 409
  mergeTalent: async (keepId, sourceId, { dryRun = false, force = false, confirmToken = null } = {}) => {
    try {
      const response = await mainApi.post(`/talents/${keepId}/merge`, {
        source_id: sourceId,
        dry_run: dryRun,
        force,
        ...(confirmToken ? { confirm_token: confirmToken } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Error merging talents:', error);
      throw error;
    }
  },


  // Обновить статус таланта
  updateTalentStatus: async (talentId, statusData) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/status`, statusData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent status:', error);
      throw error;
    }
  },

  // Обновить стадию таланта — отдельная ось от статуса, см. PATCH /talents/:id/stage
  updateTalentStage: async (talentId, stageData) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/stage`, stageData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent stage:', error);
      throw error;
    }
  },

  // Обновить менеджера таланта
  updateTalentManager: async (talentId, managerId) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/manager`, { manager_id: managerId });
      return response.data;
    } catch (error) {
      console.error('Error updating talent manager:', error);
      throw error;
    }
  },

  // Обновить привязанного пользователя таланта
  updateTalentLinkedUser: async (talentId, authUserId) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/linked-user`, { auth_user_id: authUserId });
      return response.data;
    } catch (error) {
      console.error('Error updating talent linked user:', error);
      throw error;
    }
  },

  updateTalentTools: async (talentId, tools) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/tools`, { tools });
      return response.data;
    } catch (error) {
      console.error('Error updating talent tools:', error);
      throw error;
    }
  },

  updateTalentProfessions: async (talentId, professions) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/professions`, { professions });
      return response.data;
    } catch (error) {
      console.error('Error updating talent professions:', error);
      throw error;
    }
  },

  updateTalentResponsibilities: async (talentId, responsibilities) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/responsibilities`, { responsibilities });
      return response.data;
    } catch (error) {
      console.error('Error updating talent responsibilities:', error);
      throw error;
    }
  },

  // Получить пользователей, не привязанных к талантам (для UserSelect)
  getUsersNotInTalents: async (excludeTalentId = null) => {
    try {
      const params = {};
      if (excludeTalentId) params.exclude_talent_id = excludeTalentId;
      const response = await gatewayApi.get('/talent-service/talents/users/not-in-talents', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching users not in talents:', error);
      throw error;
    }
  },

  // Обновить статус активности таланта
  updateTalentActivity: async (talentId, isActive) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/activity`, { is_active: isActive });
      return response.data;
    } catch (error) {
      console.error('Error updating talent activity:', error);
      throw error;
    }
  },

  updateTalentFlags: async (talentId, flags) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/flags`, flags);
      return response.data;
    } catch (error) {
      console.error('Error updating talent flags:', error);
      throw error;
    }
  },

  // Обновить public_visible для профессии таланта
  updateTalentProfession: async (talentId, professionId, data) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/professions/${professionId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating talent profession:', error);
      throw error;
    }
  },

  // Языки таланта — CRUD
  addTalentLanguage: async (talentId, data) => {
    try {
      const response = await mainApi.post(`/talents/${talentId}/languages`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding talent language:', error);
      throw error;
    }
  },

  updateTalentLanguage: async (talentId, languageEntryId, data) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/languages/${languageEntryId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating talent language:', error);
      throw error;
    }
  },

  deleteTalentLanguage: async (talentId, languageEntryId) => {
    try {
      const response = await mainApi.delete(`/talents/${talentId}/languages/${languageEntryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent language:', error);
      throw error;
    }
  },

  // CV таланта — CRUD
  addTalentCV: async (talentId, data) => {
    try {
      const response = await mainApi.post(`/talents/${talentId}/cv`, data);
      return response.data;
    } catch (error) {
      console.error('Error adding talent CV:', error);
      throw error;
    }
  },

  updateTalentCV: async (talentId, cvId, data) => {
    try {
      const response = await mainApi.patch(`/talents/${talentId}/cv/${cvId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating talent CV:', error);
      throw error;
    }
  },

  deleteTalentCV: async (talentId, cvId) => {
    try {
      const response = await mainApi.delete(`/talents/${talentId}/cv/${cvId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent CV:', error);
      throw error;
    }
  },

  // Проверка возможности перехода статуса
  getStatusTransition: async (fromStatusId, toStatusId) => {
    try {
      const response = await mainApi.get(`/statuses/${fromStatusId}/transitions/to/${toStatusId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting status transition:', error);
      throw error;
    }
  },



  // Получить статистику талантов
  getTalentsStats: async () => {
    try {
      const response = await mainApi.get('/talents/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching talents stats:', error);
      throw error;
    }
  },

  // Проверить уникальность контакта для Talent
  checkContactUniqueness: async (toolId, value, excludeTalentId = null) => {
    try {
      const params = {
        tool_id: toolId,
        value: value
      };
      if (excludeTalentId) {
        params.exclude_talent_id = excludeTalentId;
      }
      const response = await mainApi.get('/talents/check-contact-uniqueness', { params });
      return response.data;
    } catch (error) {
      console.error('Error checking contact uniqueness:', error);
      throw error;
    }
  },

  // Получить талантов по типу с учетом истории изменений
  getTalentsByTypeWithHistory: async (talentType, params = {}) => {
    try {
      console.log('🔍 getTalentsByTypeWithHistory called with:', { talentType, params });
      const response = await gatewayApi.get(`/talents/type/${talentType}`, { params });
      console.log('✅ getTalentsByTypeWithHistory success:', {
        status: response.status,
        data: response.data,
        talentsCount: response.data?.data?.talents?.length || 0,
        type: response.data?.data?.type,
        type_category: response.data?.data?.type_category
      });
      return response.data.data;
    } catch (error) {
      console.error('❌ getTalentsByTypeWithHistory error:', {
        talentType,
        params,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Получить ВСЕХ сотрудников (включая уволенных) - требует разрешение list-all
  getAllEmployees: async (params = {}) => {
    try {
      console.log('🔍 getAllEmployees called with params:', params);
      const response = await gatewayApi.get('/talents/type/employee/all', { params });
      console.log('✅ getAllEmployees success:', {
        status: response.status,
        data: response.data,
        employeesCount: response.data?.data?.talents?.length || 0
      });
      return response.data.data;
    } catch (error) {
      console.error('❌ getAllEmployees error:', {
        params,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Изменить тип таланта (candidate -> employee, employee -> presale и т.д.).
  // Причину подставляет TalentTypeChangeModal — второй дефолт здесь только
  // прятал бы пустое поле от разработчика
  changeTalentType: async (id, newTalentType, reason) => {
    try {
      const response = await mainApi.patch(`/talents/${id}/type`, {
        new_talent_type: newTalentType,
        reason
      });

      return response.data;

    } catch (error) {
      console.error('Change talent type error:', error);
      throw error;
    }
  },

  // ========== СОБЫТИЯ ТАЛАНТОВ ==========

  // Получить события таланта (история изменений) с пагинацией
  getTalentEvents: async (id, params = {}) => {
    try {
      const response = await gatewayApi.get(`/talents/${id}/events`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching talent events:', error);
      throw error;
    }
  },

  // Получить статистику событий таланта
  getTalentEventStats: async (talentId, days = 30) => {
    try {
      const response = await gatewayApi.get(`talent-service/talents/${talentId}/events/stats`, {
        params: { days }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching talent event stats:', error);
      throw error;
    }
  },

  // ========== КОММУНИКАЦИИ ТАЛАНТОВ ==========

  // Получить коммуникации таланта
  getTalentCommunications: async (id) => {
    try {
      const response = await gatewayApi.get(`/talents/${id}/communications`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching talent communications:', error);
      throw error;
    }
  },

  // Добавить коммуникацию к таланту
  addTalentCommunication: async (id, communicationData) => {
    try {
      const response = await mainApi.post(`/talents/${id}/communications`, communicationData);
      return response.data;
    } catch (error) {
      console.error('Error adding talent communication:', error);
      throw error;
    }
  },

  // Обновить коммуникацию таланта
  updateTalentCommunication: async (id, communicationId, communicationData) => {
    try {
      const response = await mainApi.put(`/talents/${id}/communications/${communicationId}`, communicationData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent communication:', error);
      throw error;
    }
  },

  // Удалить коммуникацию таланта
  deleteTalentCommunication: async (id, communicationId) => {
    try {
      const response = await mainApi.delete(`/talents/${id}/communications/${communicationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent communication:', error);
      throw error;
    }
  },

  // ========== ВАЖНЫЕ ДАТЫ ТАЛАНТОВ ==========

  // Получить важные даты таланта
  getTalentImportantDates: async (id, params = {}) => {
    try {
      const response = await mainApi.get(`/important-dates/Talent/${id}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent important dates:', error);
      throw error;
    }
  },

  // Добавить важную дату к таланту
  addTalentImportantDate: async (id, dateData) => {
    try {
      const response = await mainApi.post('/important-dates', {
        entityType: 'Talent',
        entityId: id,
        ...dateData
      });
      return response.data;
    } catch (error) {
      console.error('Error adding talent important date:', error);
      throw error;
    }
  },

  // Обновить важную дату таланта
  updateTalentImportantDate: async (dateId, dateData) => {
    try {
      const response = await mainApi.put(`/important-dates/${dateId}`, dateData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent important date:', error);
      throw error;
    }
  },

  // Удалить важную дату таланта
  deleteTalentImportantDate: async (dateId) => {
    try {
      const response = await mainApi.delete(`/important-dates/${dateId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent important date:', error);
      throw error;
    }
  },

  // Получить данные календаря с важными датами
  getCalendarData: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/important-dates/calendar', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      throw error;
    }
  },

  // ========== AI НАСТРОЙКИ ТАЛАНТОВ ==========

  // Получить AI настройки таланта
  getTalentAiSettings: async (id) => {
    try {
      const response = await mainApi.get(`/talents/${id}/ai-settings`);
      return response.data;
    } catch (error) {
      console.error('Error fetching talent AI settings:', error);
      throw error;
    }
  },

  // Добавить AI настройку к таланту
  addTalentAiSetting: async (id, settingData) => {
    try {
      const response = await mainApi.post(`/talents/${id}/ai-settings`, settingData);
      return response.data;
    } catch (error) {
      console.error('Error adding talent AI setting:', error);
      throw error;
    }
  },

  // Обновить AI настройку таланта
  updateTalentAiSetting: async (id, settingId, settingData) => {
    try {
      const response = await mainApi.put(`/talents/${id}/ai-settings/${settingId}`, settingData);
      return response.data;
    } catch (error) {
      console.error('Error updating talent AI setting:', error);
      throw error;
    }
  },

  // Удалить AI настройку таланта
  deleteTalentAiSetting: async (id, settingId) => {
    try {
      const response = await mainApi.delete(`/talents/${id}/ai-settings/${settingId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent AI setting:', error);
      throw error;
    }
  },

  // Автогенерация ElevenLabs voice-агента (клон голоса + prompt + создание агента)
  generateTalentAgent: async (id, options = {}) => {
    try {
      const response = await mainApi.post(`/talents/${id}/ai-settings/generate`, options);
      return response.data;
    } catch (error) {
      console.error('Error generating talent voice agent:', error);
      throw error;
    }
  },

  // Регенерация существующего ElevenLabs агента
  regenerateTalentAgent: async (id, options = {}) => {
    try {
      const response = await mainApi.post(`/talents/${id}/ai-settings/regenerate`, options);
      return response.data;
    } catch (error) {
      console.error('Error regenerating talent voice agent:', error);
      throw error;
    }
  },

  // Удаление ElevenLabs агента (options.deleteVoice — также удалить клон голоса)
  deleteTalentAgent: async (id, options = {}) => {
    try {
      const response = await mainApi.delete(`/talents/${id}/ai-settings/agent`, { data: options });
      return response.data;
    } catch (error) {
      console.error('Error deleting talent voice agent:', error);
      throw error;
    }
  },

  // Проверка уникальности slug для публичных настроек
  checkSlugUniqueness: async (slug, excludeTalentId = null) => {
    try {
      const params = {};
      if (excludeTalentId) {
        params.exclude_talent_id = excludeTalentId;
      }

      const response = await mainApi.get(`/talents/slug-check/${encodeURIComponent(slug)}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error checking slug uniqueness:', error);
      throw error;
    }
  },

  // Unique short_name and slug from first and last name — the formulas live on the backend
  generatePublicIdentifiers: async (firstName, lastName, excludeTalentId = null) => {
    try {
      const params = { first_name: firstName, last_name: lastName };
      if (excludeTalentId) {
        params.exclude_talent_id = excludeTalentId;
      }

      const response = await mainApi.get('/talents/public-identifiers', { params });
      return response.data;
    } catch (error) {
      console.error('Error generating public identifiers:', error);
      throw error;
    }
  },

  // Получить публичный каталог (employees и presale)
  // Использует API Token для доступа через API Gateway
  getPublicCatalog: async (params = {}) => {
    try {
      console.log('🔍 getPublicCatalog called with:', params);

      const response = await publicApi.get('/talent-service/public/catalog', { params });
      // Ответ: { success, data: [...], total: N } (прямой) или { success, data: { success, data: [...], total: N } } (через formatResponseMiddleware)
      const raw = response.data || {};
      const inner = raw.data;
      const items = Array.isArray(inner) ? inner : (Array.isArray(inner?.data) ? inner.data : []);
      const total = raw.total ?? inner?.total ?? null;

      console.log('✅ getPublicCatalog success:', { status: response.status, talentsCount: items.length, total });

      return { data: items, total };
    } catch (error) {
      console.error('❌ getPublicCatalog error:', {
        params,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Получить данные для фильтров публичного каталога (только те, что реально есть у талантов)
  getPublicCatalogFilters: async () => {
    try {
      const response = await publicApi.get('/talent-service/public/catalog/filters');
      const payload = response.data?.data || response.data || {};
      return payload?.data || payload || {};
    } catch (error) {
      console.error('❌ getPublicCatalogFilters error:', error.message);
      return {};
    }
  },

  // Получить похожих талантов по slug
  // Использует API Token для доступа через API Gateway
  getSimilarTalents: async (slug) => {
    try {
      const response = await publicApi.get(`/talent-service/public/catalog/${slug}/similar`);
      const raw = response.data || {};
      const inner = raw.data;
      const items = Array.isArray(inner) ? inner : (Array.isArray(inner?.data) ? inner.data : []);
      return { data: items, total: items.length };
    } catch (error) {
      console.error('❌ getSimilarTalents error:', { slug, error: error.message });
      return { data: [], total: 0 };
    }
  },

  // Получить публичный профиль таланта по slug
  // Использует API Token для доступа через API Gateway
  getPublicTalentBySlug: async (slug) => {
    try {
      console.log('🔍 getPublicTalentBySlug called with:', slug);

      const response = await publicApi.get(`/talent-service/public/catalog/${slug}`);
      console.log('✅ getPublicTalentBySlug success:', {
        status: response.status,
        talent_id: response.data?.data?.data?.talent_id || response.data?.data?.talent_id,
      });

      // Обрабатываем структуру ответа от API Gateway
      // Структура: { success: true, data: { success: true, data: {...} } }
      if (response.data?.data?.data) {
        return response.data.data.data;
      }
      return response.data?.data || null;
    } catch (error) {
      console.error('❌ getPublicTalentBySlug error:', {
        slug,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Получить справочные данные для публичного каталога (без JWT, через API Token)
  getPublicTools: async () => {
    try {
      const response = await publicApi.get('/libraries/tools', { params: { all: true, isCommon: 1 } });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching public tools:', error.message);
      return [];
    }
  },

  getPublicLanguages: async () => {
    try {
      const response = await publicApi.get('/libraries/language', { params: { all: true, isCommon: 1 } });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching public languages:', error.message);
      return [];
    }
  },

  getPublicDepartments: async () => {
    try {
      const response = await publicApi.get('/libraries/department', { params: { all: true, isCommon: 1 } });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching public departments:', error.message);
      return [];
    }
  },

  getPublicProfessions: async () => {
    try {
      const response = await publicApi.get('/libraries/professions', { params: { all: true, isCommon: 1 } });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching public professions:', error.message);
      return [];
    }
  },

  // ========== MY PROFILE ==========

  // Получить профиль текущего пользователя
  getMyProfile: async () => {
    try {
      const response = await mainApi.get('/talents/my-profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching my profile:', error);
      throw error;
    }
  },

  // Обновить профиль текущего пользователя
  updateMyProfile: async (profileData) => {
    try {
      const response = await mainApi.put('/talents/my-profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating my profile:', error);
      throw error;
    }
  }

};

// ==================== STATUS METHODS ====================

const statusService = {
  // Получить статусы по типу таланта (новый универсальный метод)
  getStatusesByType: async (talentType, params = {}) => {
    try {
      const response = await mainApi.get(`/statuses/type/${talentType}`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Получить все статусы
  getAllStatuses: async (params = {}) => {
    try {
      const response = await mainApi.get('/statuses', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching statuses:', error);
      throw error;
    }
  },


  // Обновить статус
  updateStatus: async (id, statusData) => {
    try {
      const response = await mainApi.put(`/statuses/${id}`, statusData);
      return response.data;
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  },

  // Удалить статус
  deleteStatus: async (id) => {
    try {
      const response = await mainApi.delete(`/statuses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting status:', error);
      throw error;
    }
  },

  // Обновить порядок статусов
  updateStatusOrder: async (updates) => {
    try {
      const response = await mainApi.put('/statuses/order', { updates });
      return response.data;
    } catch (error) {
      console.error('Error updating status order:', error);
      throw error;
    }
  },


  // Получить переходы статусов
  getStatusTransitions: async (statusId) => {
    try {
      const response = await mainApi.get(`/statuses/${statusId}/transitions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching status transitions:', error);
      throw error;
    }
  },

  // Получить все переходы статусов
  getAllStatusTransitions: async (talentType = null) => {
    try {
      const params = talentType ? { talent_type: talentType } : {};
      const response = await mainApi.get('/statuses/transitions/all', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching all status transitions:', error);
      throw error;
    }
  },

  // Получить граф переходов
  getTransitionGraph: async (talentType = null) => {
    try {
      const params = talentType ? { talent_type: talentType } : {};
      const response = await mainApi.get('/statuses/transitions/graph', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching transition graph:', error);
      throw error;
    }
  },

  // Создать переход статусов
  createStatusTransition: async (transitionData) => {
    try {
      const response = await mainApi.post('/statuses/transitions', transitionData);
      return response.data;
    } catch (error) {
      console.error('Error creating status transition:', error);
      throw error;
    }
  },

  // Обновить переход статусов
  updateStatusTransition: async (transitionId, updateData) => {
    try {
      const response = await mainApi.put(`/statuses/transitions/${transitionId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating status transition:', error);
      throw error;
    }
  },

  // Удалить переход статусов
  deleteStatusTransition: async (transitionId) => {
    try {
      const response = await mainApi.delete(`/statuses/transitions/${transitionId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting status transition:', error);
      throw error;
    }
  },

  // Принудительная синхронизация статусов из Libraries Service
  forceSyncStatuses: async () => {
    try {
      const response = await mainApi.post('/sync/force-sync');
      return response.data;
    } catch (error) {
      console.error('Error forcing status sync:', error);
      throw error;
    }
  },

  // Получить статистику синхронизации
  getSyncStats: async () => {
    try {
      const response = await mainApi.get('/sync/stats');
      return response.data;
    } catch (error) {
      console.error('Error getting sync stats:', error);
      throw error;
    }
  },

  getUsedCityIds: async () => {
    try {
      const response = await mainApi.get('/talents/cities/used');
      return response.data;
    } catch (error) {
      console.error('Error fetching used city ids:', error);
      throw error;
    }
  }
};

// ==================== STAGE METHODS ====================
// Стадия — отдельная ось от статуса: "где талант находится" против "что с ним
// на этой стадии". См. src/models/TalentStage.js в talent-service.

const stageService = {
  // Все стадии (по умолчанию только активные).
  // isCommon: 1 по умолчанию — общий справочник, обходит строгое право
  // stage.list/read (см. src/routes/talentStage.js): карточка/форма
  // таланта, список талантов и страница статусов вызывают это без
  // отдельного права на стадии, только с правом на то, что редактируют
  // (талант или статус). Явно передайте isCommon: undefined/0, если нужен
  // строгий листинг именно под правом stage.* (сегодня не нужен нигде).
  getStages: async (params = {}) => {
    try {
      const response = await mainApi.get('/stages', { params: { isCommon: 1, ...params } });
      return response.data;
    } catch (error) {
      console.error('Error fetching stages:', error);
      throw error;
    }
  },

  // Статусы, разрешённые на стадии, в порядке display_order — первый элемент
  // это дефолтный статус стадии. Используется контролом Status после выбора Stage.
  getStatusesForStage: async (stageId) => {
    try {
      // isCommon: 1 — общий справочник, обходит строгий status.list (см.
      // src/routes/talentStatus.js), доступ через право редактировать
      // самого таланта — как у соседнего /statuses/type/:talent_type.
      const response = await mainApi.get('/statuses', { params: { stage_id: stageId, isCommon: 1 } });
      return response.data;
    } catch (error) {
      console.error('Error fetching statuses for stage:', error);
      throw error;
    }
  },

  createStage: async (stageData) => {
    try {
      const response = await mainApi.post('/stages', stageData);
      return response.data;
    } catch (error) {
      console.error('Error creating stage:', error);
      throw error;
    }
  },

  updateStage: async (id, stageData) => {
    try {
      const response = await mainApi.put(`/stages/${id}`, stageData);
      return response.data;
    } catch (error) {
      console.error('Error updating stage:', error);
      throw error;
    }
  },

  deleteStage: async (id) => {
    try {
      const response = await mainApi.delete(`/stages/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting stage:', error);
      throw error;
    }
  },

  restoreStage: async (id) => {
    try {
      const response = await mainApi.post(`/stages/${id}/restore`);
      return response.data;
    } catch (error) {
      console.error('Error restoring stage:', error);
      throw error;
    }
  },

  // Полный набор статусов стадии с display_order — для формы редактирования стадии
  getStageStatusLinks: async (stageId) => {
    try {
      const response = await mainApi.get(`/stages/${stageId}/statuses`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stage status links:', error);
      throw error;
    }
  },

  // Полная замена набора статусов стадии. statusIds — порядок в массиве
  // становится display_order (первый = дефолт стадии)
  setStageStatuses: async (stageId, statusIds) => {
    try {
      const response = await mainApi.put(`/stages/${stageId}/statuses`, { status_ids: statusIds });
      return response.data;
    } catch (error) {
      console.error('Error setting stage statuses:', error);
      throw error;
    }
  },

  // Плотная перенумерация 1..N после drag-переупорядочивания.
  // updates: [{ id, stage_order }, ...] — весь список целиком
  updateStageOrder: async (updates) => {
    try {
      const response = await mainApi.put('/stages/order', { updates });
      return response.data;
    } catch (error) {
      console.error('Error updating stage order:', error);
      throw error;
    }
  }
};

// ==================== JOB APPLICATION METHODS ====================

const jobApplicationService = {
  // Получить все заявки с пагинацией и фильтрацией
  getJobApplications: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talents/job-applications', { params });
      
      // Обрабатываем новую структуру ответа
      if (response.data && response.data.success && response.data.data) {
        return {
          success: true,
          data: response.data.data.data, // Извлекаем массив job applications
          pagination: response.data.data.pagination, // Извлекаем пагинацию
          message: response.data.message || 'Success'
        };
      }
      
      // Fallback для старой структуры
      return response.data;
    } catch (error) {
      console.error('Error fetching job applications:', error);
      throw error;
    }
  },

  // Получить заявку по ID
  getJobApplicationById: async (id) => {
    try {
      const response = await mainApi.get(`/job-applications/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching job application:', error);
      throw error;
    }
  },

    // Получить заявку по ID
    getJobApplicationByIdWithRelations: async (id) => {
      try {
        const response = await gatewayApi.get(`/talents/job-applications/${id}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching job application:', error);
        throw error;
      }
    },
  // Создать новую заявку
  createJobApplication: async (data) => {
    try {
      const response = await mainApi.post('/job-applications', data);
      return response.data;
    } catch (error) {
      console.error('Error creating job application:', error);
      throw error;
    }
  },

  // Обновить заявку
  updateJobApplication: async (id, data) => {
    try {
      const response = await mainApi.put(`/job-applications/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating job application:', error);
      throw error;
    }
  },

  // Удалить заявку
  deleteJobApplication: async (id) => {
    try {
      const response = await mainApi.delete(`/job-applications/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job application:', error);
      throw error;
    }
  },

  // Конвертировать Job Application в Talent
  convertToTalent: async (id, talentType, additionalData = {}) => {
    try {
      const response = await mainApi.post(`/job-applications/${id}/convert-to-talent`, {
        talent_type: talentType,
        additional_data: additionalData
      });
      return response.data;
    } catch (error) {
      console.error('Error converting Job Application to Talent:', error);
      throw error;
    }
  },

  // Получить статистику заявок
  getJobApplicationStats: async () => {
    try {
      const response = await mainApi.get('/job-applications/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching job application stats:', error);
      throw error;
    }
  },

  // Проверить уникальность контакта
  checkContactUniqueness: async (toolId, value, excludeJobApplicationId = null) => {
    try {
      const params = {
        tool_id: toolId,
        value: value
      };
      if (excludeJobApplicationId) {
        params.exclude_job_application_id = excludeJobApplicationId;
      }
      const response = await mainApi.get('/job-applications/check-contact-uniqueness', { params });
      return response.data;
    } catch (error) {
      console.error('Error checking contact uniqueness:', error);
      throw error;
    }
  },

  // ========== КОНТАКТЫ ЗАЯВОК ==========

  // Получить контакты заявки
  getJobApplicationContacts: async (id) => {
    try {
    const response = await gatewayApi.get(`/talents/job-applications/${id}/contacts`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job application contacts:', error);
      throw error;
    }
  },

  // Добавить контакт к заявке
  addJobApplicationContact: async (id, contactData) => {
    try {
      const response = await mainApi.post(`/job-applications/${id}/contacts`, contactData);
      return response.data;
    } catch (error) {
      console.error('Error adding job application contact:', error);
      throw error;
    }
  },

  // Обновить контакт заявки
  updateJobApplicationContact: async (id, contactId, contactData) => {
    try {
      const response = await mainApi.put(`/job-applications/${id}/contacts/${contactId}`, contactData);
      return response.data;
    } catch (error) {
      console.error('Error updating job application contact:', error);
      throw error;
    }
  },

  // Удалить контакт заявки
  deleteJobApplicationContact: async (id, contactId) => {
    try {
      const response = await mainApi.delete(`/job-applications/${id}/contacts/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job application contact:', error);
      throw error;
    }
  },

  // ========== КОММУНИКАЦИИ ЗАЯВОК ==========

  // Получить коммуникации заявки
  getJobApplicationCommunications: async (id) => {
    try {
      const response = await gatewayApi.get(`/talents/job-applications/${id}/communications`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job application communications:', error);
      throw error;
    }
  },

  // Добавить коммуникацию к заявке
  addJobApplicationCommunication: async (id, communicationData) => {
    try {
      const response = await mainApi.post(`/job-applications/${id}/communications`, communicationData);
      return response.data;
    } catch (error) {
      console.error('Error adding job application communication:', error);
      throw error;
    }
  },

  // Обновить коммуникацию заявки
  updateJobApplicationCommunication: async (id, communicationId, communicationData) => {
    try {
      const response = await mainApi.put(`/job-applications/${id}/communications/${communicationId}`, communicationData);
      return response.data;
    } catch (error) {
      console.error('Error updating job application communication:', error);
      throw error;
    }
  },

  // Удалить коммуникацию заявки
  deleteJobApplicationCommunication: async (id, communicationId) => {
    try {
      const response = await mainApi.delete(`/job-applications/${id}/communications/${communicationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job application communication:', error);
      throw error;
    }
  },

  // ========== ВАЖНЫЕ ДАТЫ ЗАЯВОК ==========

  // Получить важные даты заявки
  getJobApplicationImportantDates: async (id, params = {}) => {
    try {
      const response = await mainApi.get(`/important-dates/JobApplication/${id}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching job application important dates:', error);
      throw error;
    }
  },

  // Добавить важную дату к заявке
  addJobApplicationImportantDate: async (id, dateData) => {
    try {
      const response = await mainApi.post('/important-dates', {
        entityType: 'JobApplication',
        entityId: id,
        ...dateData
      });
      return response.data;
    } catch (error) {
      console.error('Error adding job application important date:', error);
      throw error;
    }
  },

  // Обновить важную дату заявки
  updateJobApplicationImportantDate: async (dateId, dateData) => {
    try {
      const response = await mainApi.put(`/important-dates/${dateId}`, dateData);
      return response.data;
    } catch (error) {
      console.error('Error updating job application important date:', error);
      throw error;
    }
  },

  // Удалить важную дату заявки
  deleteJobApplicationImportantDate: async (dateId) => {
    try {
      const response = await mainApi.delete(`/important-dates/${dateId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job application important date:', error);
      throw error;
    }
  },

  // ========== INLINE EDITING ==========

  // Обновить статус заявки
  updateJobApplicationStatus: async (jobApplicationId, statusId) => {
    try {
      const response = await mainApi.patch(`/job-applications/${jobApplicationId}/status`, { status_id: statusId });
      return response.data;
    } catch (error) {
      console.error('Error updating job application status:', error);
      throw error;
    }
  },

  // Обновить менеджера заявки
  updateJobApplicationManager: async (jobApplicationId, managerId) => {
    try {
      const response = await mainApi.patch(`/job-applications/${jobApplicationId}/manager`, { manager_id: managerId });
      return response.data;
    } catch (error) {
      console.error('Error updating job application manager:', error);
      throw error;
    }
  },

  // ========== СОБЫТИЯ ЗАЯВОК ==========

  // Получить события заявки с пагинацией
  getJobApplicationEvents: async (jobApplicationId, params = {}) => {
    try {
      // агрегированный роут гейтвея: подставляет автора события и названия вместо id
      const response = await gatewayApi.get(`/talents/job-applications/${jobApplicationId}/events`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job application events:', error);
      throw error;
    }
  },

  // Получить статистику событий заявки
  getJobApplicationEventStats: async (jobApplicationId, days = 30) => {
    try {
      const response = await gatewayApi.get(`/talent-service/job-applications/${jobApplicationId}/events/stats`, {
        params: { days }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job application event stats:', error);
      throw error;
    }
  }
};

// ==================== NOTES METHODS ====================

const notesService = {
  // Получить заметки для сущности с пагинацией и фильтрацией
  getNotesByEntity: async (entityType, entityId, params = {}) => {
    try {
      const response = await mainApi.get(`/notes/${entityType}/${entityId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  },

  // Получить заметку по ID
  getNoteById: async (id) => {
    try {
      const response = await mainApi.get(`/notes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching note:', error);
      throw error;
    }
  },

  // Создать новую заметку
  createNote: async (data) => {
    try {
      const response = await mainApi.post('/notes', data);
      return response.data;
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  },

  // Обновить заметку
  updateNote: async (id, data) => {
    try {
      const response = await mainApi.put(`/notes/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  // Удалить заметку
  deleteNote: async (id) => {
    try {
      const response = await mainApi.delete(`/notes/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  // Получить заметки по типу
  getNotesByType: async (noteType, params = {}) => {
    try {
      const response = await mainApi.get(`/notes/by-type/${noteType}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching notes by type:', error);
      throw error;
    }
  }
};

// ==================== USERS METHODS ====================

const userService = {
  // Получить всех пользователей через API Gateway
  getAllUsers: async () => {
    try {
      const response = await gatewayApi.get('/users', { 
        params: { all: true, isCommon: 1 , isActive: true} 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Получить пользователей, которых нет в таблице Talents
  getUsersNotInTalents: async (params = {}) => {
    try {
      console.log('🔍 API: getUsersNotInTalents called with params:', params);
      const response = await mainApi.get('/talents/users/not-in-talents', { 
        params: {
          search: params.search,
          exclude_talent_id: params.exclude_talent_id
        }
      });
    
      return response.data;
    } catch (error) {
      console.error('Error fetching users not in talents:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  // Получить список ролей из auth service
  getRoles: async () => {
    try {
      const response = await gatewayApi.get('/auth-service/roles', { 
        params: { 
          all: true, 
          active: true,
          service: SERVICE_NAME
        } 
      });
      
      if (response.data && response.data.roles) {
        // Обрабатываем ответ от roles API
        const roles = response.data.roles.map(role => ({
          value: role.name,
          label: role.name.charAt(0).toUpperCase() + role.name.slice(1)
        }));
        
        return {
          success: true,
          data: roles
        };
      }
      
      // Если роли не получены, возвращаем пустой массив
      return {
        success: true,
        data: []
      };
    } catch (error) {
      console.error('Error fetching roles:', error);
      // При ошибке возвращаем пустой массив
      return {
        success: true,
        data: []
      };
    }
  },

  // Обновление статуса активности пользователя в Auth User Service
  updateUserActivity: async (userId, isActive) => {
    try {
      if (!userId) {
        console.warn('⚠️ Cannot update user activity: userId is missing');
        return { success: false, error: 'User ID is required' };
      }

      // Выбираем роут в зависимости от статуса активности
      const endpoint = isActive ? `/auth-service/users/${userId}/unban` : `/auth-service/users/${userId}/ban`;
      const response = await gatewayApi.patch(endpoint, {});
      
      console.log(`✅ Successfully updated user ${isActive ? 'unban' : 'ban'} status:`, {
        userId,
        isActive,
        endpoint,
        response: response.data
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`❌ Error updating user activity:`, {
        userId,
        isActive,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      // Не пробрасываем ошибку дальше, чтобы не блокировать основную операцию
      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  }
};

// ==================== ACCOUNT METHODS ====================

const accountService = {
  // Получить данные аккаунта пользователя через API Gateway
  getUserAccount: async (userId) => {
    try {
      console.log(`💼 Fetching account data for user ${userId}...`);
      const response = await gatewayApi.get(`/account-service/accounts/user/${userId}`);
      
      console.log(`✅ Account data fetched successfully:`, response.data);
      
      // Обрабатываем структуру ответа: response.data.data.data - массив аккаунтов
      // Структура: { success: true, data: { success: true, data: [...] } }
      let accountsData = [];
      
      if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        accountsData = response.data.data.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        accountsData = response.data;
      }
      
      return {
        success: true,
        data: accountsData
      };
    } catch (error) {
      console.error(`❌ Error fetching account data:`, {
        userId,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Возвращаем ошибку, но не пробрасываем её, чтобы не блокировать отображение профиля
      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },
  
  // Получить данные аккаунта текущего пользователя через API Gateway (self)
  getUserSelfAccounts: async (userId) => {
    try {
      console.log(`💼 Fetching self account data for user ${userId}...`);
      const response = await gatewayApi.get(`/account-service/accounts/user/${userId}/self`);
      
      console.log(`✅ Self account data fetched successfully:`, response.data);
      
      // Обрабатываем структуру ответа: response.data.data.data - массив аккаунтов
      // Структура: { success: true, data: { success: true, data: [...] } }
      let accountsData = [];
      
      if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        accountsData = response.data.data.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        accountsData = response.data;
      }
      
      return {
        success: true,
        data: accountsData
      };
    } catch (error) {
      console.error(`❌ Error fetching self account data:`, {
        userId,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Возвращаем ошибку, но не пробрасываем её, чтобы не блокировать отображение профиля
      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Назначить аккаунты пользователю
  attachAccounts: async (userId, accountIds) => {
    try {
      console.log(`💼 Attaching accounts to user ${userId}...`, { accountIds });
      const response = await gatewayApi.post(`/account-service/accounts/user/${userId}/attach`, {
        account_ids: accountIds
      });
      
      console.log(`✅ Accounts attached successfully:`, response.data);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`❌ Error attaching accounts:`, {
        userId,
        accountIds,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Получить мои социальные аккаунты (My Accounts tab)
  getMyAccounts: async () => {
    try {
      console.log(`💼 Fetching my accounts...`);
      const response = await gatewayApi.get(`/account-service/accounts/my-accounts`);

      console.log(`✅ My accounts fetched successfully:`, response.data);

      // Обрабатываем структуру ответа
      let accountsData = [];

      if (response.data?.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        accountsData = response.data;
      }

      return {
        success: true,
        data: accountsData
      };
    } catch (error) {
      console.error(`❌ Error fetching my accounts:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  updateSubAiLimitDate: async (subAiId, limitDate) => {
    try {
      const response = await gatewayApi.patch(
        `/account-service/sub-ai/${subAiId}/limit-date`,
        { limit_date: limitDate }
      );
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error(`❌ Error updating SubAi limit date:`, error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || error.message
      };
    }
  },

  // Получить social accounts где пользователь назначен как Assigned User
  getSocialAccountsByAssignedUserId: async (userId) => {
    try {
      console.log(`💼 Fetching social accounts by assigned user ID: ${userId}`);
      const response = await gatewayApi.get(`/account-service/social-accounts/user/${userId}`);

      console.log(`✅ Social accounts by assigned user ID fetched successfully:`, response.data);

      let socialAccountsData = [];

      // Handle nested structure: response.data.data.data or response.data.data
      if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        socialAccountsData = response.data.data.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        socialAccountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        socialAccountsData = response.data;
      }

      return {
        success: true,
        data: socialAccountsData
      };
    } catch (error) {
      console.error(`❌ Error fetching social accounts by assigned user ID:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Получить social accounts для авторизированного пользователя (self)
  getSocialAccountsByAssignedUserIdSelf: async (userId) => {
    try {
      console.log(`📱 Fetching self social accounts data for user ${userId}...`);
      const response = await gatewayApi.get(`/account-service/social-accounts/user/${userId}/self`);

      console.log(`✅ Self social accounts data fetched successfully:`, response.data);

      // Handle nested structure: response.data.data.data or response.data.data
      const data = response.data?.data?.data || response.data?.data || [];

      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        message: response.data?.message
      };
    } catch (error) {
      console.error(`❌ Error fetching self social accounts:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch social accounts',
        data: []
      };
    }
  },

  // Получить AccountCredentials, назначенные текущему пользователю (self)
  getAssignedCredentialsSelf: async (userId) => {
    try {
      console.log(`🔑 Fetching assigned credentials for user ${userId}...`);
      const response = await gatewayApi.get(`/account-service/credentials/user/${userId}/self`);
      const data = response.data?.data?.data || response.data?.data || response.data || [];
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
      };
    } catch (error) {
      console.error(`❌ Error fetching assigned credentials:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch credentials',
        data: [],
      };
    }
  },

  // Получить AccountCredentials, назначенные пользователю — для просмотра профиля таланта
  getAssignedCredentialsByUserId: async (userId) => {
    try {
      const response = await gatewayApi.get(`/account-service/credentials/user/${userId}`);
      const data = response.data?.data?.data || response.data?.data || response.data || [];
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch credentials',
        data: [],
      };
    }
  },

  // Прикрепить social accounts к пользователю
  attachSocialAccountsToUser: async (userId, socialAccountIds) => {
    try {
      console.log(`💼 Attaching social accounts to user ${userId}:`, socialAccountIds);
      const response = await gatewayApi.post(`/account-service/social-accounts/user/${userId}/attach`, {
        social_account_ids: socialAccountIds
      });

      console.log(`✅ Social accounts attached successfully:`, response.data);

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message
      };
    } catch (error) {
      console.error(`❌ Error attaching social accounts to user:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.response?.data?.message || error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Получить все social accounts (для выбора при прикреплении)
  getAllSocialAccounts: async (params = {}) => {
    try {
      console.log(`💼 Fetching all social accounts...`);
      const response = await gatewayApi.get(`/account-service/social-accounts`, { params });

      console.log(`✅ All social accounts fetched successfully:`, response.data);

      let socialAccountsData = [];

      if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        socialAccountsData = response.data.data.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        socialAccountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        socialAccountsData = response.data;
      }

      return {
        success: true,
        data: socialAccountsData,
        pagination: response.data?.pagination || response.data?.data?.pagination
      };
    } catch (error) {
      console.error(`❌ Error fetching all social accounts:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // ─── Social Account Notes & Notifications (Self — без permission, только assigned user) ───

  updateSocialAccountNotificationsSelf: async (socialAccountId, isNotifications) => {
    try {
      const response = await gatewayApi.patch(`/account-service/social-accounts/${socialAccountId}/self/notifications`, { is_notifications: isNotifications });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  getSocialAccountNotesSelf: async (socialAccountId) => {
    try {
      const response = await gatewayApi.get(`/account-service/social-accounts/${socialAccountId}/self/notes`);
      const data = response.data?.data || [];
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message, data: [] };
    }
  },

  createSocialAccountNoteSelf: async (socialAccountId, text) => {
    try {
      const response = await gatewayApi.post(`/account-service/social-accounts/${socialAccountId}/self/notes`, { text });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  updateSocialAccountNoteSelf: async (socialAccountId, noteId, text) => {
    try {
      const response = await gatewayApi.put(`/account-service/social-accounts/${socialAccountId}/self/notes/${noteId}`, { text });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  deleteSocialAccountNoteSelf: async (socialAccountId, noteId) => {
    try {
      await gatewayApi.delete(`/account-service/social-accounts/${socialAccountId}/self/notes/${noteId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  // ─── Social Account Notes & Notifications (Admin — с permission checks) ───

  updateSocialAccountNotifications: async (socialAccountId, isNotifications) => {
    try {
      const response = await gatewayApi.put(`/account-service/social-accounts/${socialAccountId}`, { is_notifications: isNotifications });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  getSocialAccountNotes: async (socialAccountId) => {
    try {
      const response = await gatewayApi.get(`/account-service/social-accounts/${socialAccountId}/notes`);
      const data = response.data?.data || [];
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message, data: [] };
    }
  },

  createSocialAccountNote: async (socialAccountId, text) => {
    try {
      const response = await gatewayApi.post(`/account-service/social-accounts/${socialAccountId}/notes`, { text });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  updateSocialAccountNote: async (socialAccountId, noteId, text) => {
    try {
      const response = await gatewayApi.put(`/account-service/social-accounts/${socialAccountId}/notes/${noteId}`, { text });
      return { success: true, data: response.data?.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  deleteSocialAccountNote: async (socialAccountId, noteId) => {
    try {
      await gatewayApi.delete(`/account-service/social-accounts/${socialAccountId}/notes/${noteId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  updateSocialAccountForOwnedAccount: async (id, data) => {
    try {
      const response = await gatewayApi.put(`/account-service/social-accounts/${id}/self`, data);
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message,
      };
    }
  },

  createSocialAccountForOwnedAccount: async (accountId, data) => {
    try {
      const response = await gatewayApi.post(`/account-service/social-accounts/accounts/${accountId}/self`, data);
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message,
      };
    }
  },

  // Получить аккаунты по owner_id (где я владелец)
  getAccountsByOwnerId: async (ownerId) => {
    try {
      console.log(`💼 Fetching accounts by owner ID: ${ownerId}`);
      const response = await gatewayApi.get(`/account-service/accounts/owner/${ownerId}`);

      console.log(`✅ Accounts by owner ID fetched successfully:`, response.data);

      // Обрабатываем структуру ответа
      let accountsData = [];

      if (response.data?.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        accountsData = response.data;
      }

      return {
        success: true,
        data: accountsData
      };
    } catch (error) {
      console.error(`❌ Error fetching accounts by owner ID:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Создать аккаунт (без social account)
  createAccountOnly: async (accountData) => {
    try {
      console.log(`💼 Creating account...`);
      const response = await gatewayApi.post(`/account-service/accounts/only`, accountData);

      console.log(`✅ Account created successfully:`, response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error(`❌ Error creating account:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.response?.data?.message || error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Обновить аккаунт (без social account)
  updateAccountOnly: async (accountId, accountData) => {
    try {
      console.log(`💼 Updating account ${accountId}...`);
      const response = await gatewayApi.put(`/account-service/accounts/${accountId}/only`, accountData);

      console.log(`✅ Account updated successfully:`, response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error(`❌ Error updating account:`, {
        accountId,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.response?.data?.message || error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Получить список всех доступных аккаунтов
  getAllAccounts: async (params = {}) => {
    try {
      console.log(`💼 Fetching all available accounts...`);
      const response = await gatewayApi.get(`/account-service/accounts`, { params });

      console.log(`✅ All accounts fetched successfully:`, response.data);

      // Обрабатываем структуру ответа
      let accountsData = [];

      if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        accountsData = response.data.data.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        accountsData = response.data;
      }

      return {
        success: true,
        data: accountsData
      };
    } catch (error) {
      console.error(`❌ Error fetching all accounts:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      };
    }
  },

  // Получить credential sets аккаунта по tool_id (для выбора Discord credentials)
  getCredentialsByToolId: async (accountId, toolId) => {
    try {
      const response = await gatewayApi.get(`/account-service/accounts/${accountId}/credentials`, {
        params: { toolId },
      });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching credentials by tool:', error);
      throw error;
    }
  },

  getAllCredentialsByToolId: async (toolId) => {
    try {
      const response = await gatewayApi.get('/account-service/credentials/by-tool', {
        params: { toolId },
      });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching all credentials by tool:', error);
      throw error;
    }
  },
};

// ==================== COURSE METHODS ====================

const courseService = {
  // Получить курсы пользователя
  getUserAllPlatform: async (userId, token, sections = 'courses') => {
    try {
      const response = await gatewayApi.get(`/users/${userId}/profile`, {
        params: { sections }
      });

      if (!response.data || !response.data.platforms) {
        console.warn('No platforms data in response:', response.data);
        return [];
      }

      const platforms = response.data.platforms || [];
      console.log(`📚 Found ${platforms.length} platforms for user ${userId}`);
      
      return { 
        platforms: platforms,
        success: true,
      };
    } catch (error) {
      console.error('Error fetching user courses:', error);
      if (error.response?.status === 404) {
        console.warn('User profile not found, returning empty courses array');
        return [];
      }
      throw error;
    }
  },

};

// ==================== LIBRARIES METHODS ====================

const librariesService = {
  // Получить все страны
  getCountries: async () => {
    try {
      console.log('🌍 Fetching countries from Libraries API...');
      const response = await gatewayApi.get('/libraries/countries', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Countries fetched successfully:', response.data);
      
      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error fetching countries:', error);
      throw error;
    }
  },

  // Получить города по ID страны
  getCitiesByCountry: async (countryId) => {
    try {
      console.log(`🏙️ Fetching cities for country ${countryId} from Libraries API...`);
      const response = await gatewayApi.get(`/libraries/cities`, {
        params: {
          filters: JSON.stringify({ country_id: countryId }),
          all: true,
          isCommon: 1,
          isShort: true
        }
      });
      console.log(`✅ Cities for country ${countryId} fetched successfully:`, response.data);
      
      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`❌ Error fetching cities for country ${countryId}:`, error);
      throw error;
    }
  },

  // Получить города по списку ID
  getCitiesByIds: async (ids) => {
    try {
      const response = await gatewayApi.get('/libraries/cities', {
        params: { ids: ids.join(','), isCommon: 1, isShort: true }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error fetching cities by ids:', error);
      throw error;
    }
  },

  // Получить все города (если нужен полный список)
  getAllCities: async () => {
    try {
      console.log('🏙️ Fetching all cities from Libraries API...');
      const response = await gatewayApi.get('/libraries/cities', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ All cities fetched successfully:', response.data);
      
      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error fetching all cities:', error);
      throw error;
    }
  },

  // Получить все инструменты контактов
  getTools: async (params = {}) => {
    try {
      console.log('🔧 Fetching tools from Libraries API...');

      // Объединяем переданные параметры с параметрами по умолчанию
      const defaultParams = { all: true, isCommon: 1, isShort: true };
      const requestParams = { ...defaultParams, ...params };

      const response = await gatewayApi.get('/libraries/tools', {
        params: requestParams
      });
      console.log('✅ Tools fetched successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error fetching tools:', error);
      throw error;
    }
  },

  // Получить все обязанности (responsibilities)
  getResponsibilities: async (params = {}) => {
    try {
      console.log('📋 Fetching responsibilities from Libraries API...');

      const defaultParams = { all: true, isCommon: 1, isShort: true };
      const requestParams = { ...defaultParams, ...params };

      const response = await gatewayApi.get('/libraries/responsibilities', {
        params: requestParams
      });
      console.log('✅ Responsibilities fetched successfully:', response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching responsibilities:', error);
      throw error;
    }
  },

  createTool: async (toolData) => {
    try {
      console.log('➕ Creating tool in Libraries API...', toolData);

      const response = await gatewayApi.post('/libraries/tools', toolData);
      console.log('✅ Tool created successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error creating tool:', error);
      throw error;
    }
  },

  // Получить инструменты для контактов (централизованный метод)
  // Единая точка для изменения типов тулов контактов
  getContactTools: async () => {
    try {
      console.log('📞 Fetching contact tools from Libraries API...');

      // Централизованное место для определения типов тулов контактов
      // Если нужно изменить типы - изменяем только здесь
      const toolTypeNames = "Contacts,Messenger,Social network";

      const response = await librariesService.getTools({ toolTypeNames });

      console.log('✅ Contact tools fetched successfully:', response.data);

      return response;
    } catch (error) {
      console.error('❌ Error fetching contact tools:', error);
      throw error;
    }
  },

  // Получить инструменты для ссылок (централизованный метод)
  // Единая точка для изменения типов тулов ссылок
  getLinkTools: async () => {
    try {
      console.log('🔗 Fetching link tools from Libraries API...');

      // Централизованное место для определения типов тулов ссылок
      // Если нужно изменить типы - изменяем только здесь
      const toolTypeNames = "Communication & Collaboration tools,Platform";

      const response = await librariesService.getTools({ toolTypeNames });

      console.log('✅ Link tools fetched successfully:', response.data);

      return response;
    } catch (error) {
      console.error('❌ Error fetching link tools:', error);
      throw error;
    }
  },

  // Получить все профессии с информацией о департаментах
  getProfessions: async () => {
    try {
      console.log('💼 Fetching professions with departments from Libraries API...');
      const response = await gatewayApi.get('/libraries/professions', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Professions fetched successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching professions with departments:', error);
      throw error;
    }
  },

  // Получить все similar термы профессий (term_type=similar, belongs=Profession)
  getAllSimilarTerms: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/libraries/professions/similar', { params });
      return {
        success: true,
        data: response.data?.data ?? response.data ?? []
      };
    } catch (error) {
      console.error('❌ Error fetching similar terms:', error);
      throw error;
    }
  },

  // Получить все similar термы responsibilities (term_type=similar, belongs=Responsibility)
  getAllSimilarResponsibilityTerms: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/libraries/responsibilities/similar', { params });
      return {
        success: true,
        data: response.data?.data ?? response.data ?? []
      };
    } catch (error) {
      console.error('❌ Error fetching similar responsibility terms:', error);
      throw error;
    }
  },

  // Получить все департаменты
  getDepartments: async () => {
    try {
      console.log('🏢 Fetching departments from Libraries API...');
      const response = await gatewayApi.get('/libraries/department', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Departments fetched successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      throw error;
    }
  },

  // Получить все позиции
  getPositions: async () => {
    try {
      console.log('👔 Fetching positions from Libraries API...');
      const response = await gatewayApi.get('/libraries/positions', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Positions fetched successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching positions:', error);
      throw error;
    }
  },

  // Получить все индустрии
  getIndustries: async () => {
    try {
      console.log('🏭 Fetching industries from Libraries API...');
      const response = await gatewayApi.get('/libraries/industries', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Industries fetched successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching industries:', error);
      throw error;
    }
  },

  // Получить все суб-индустрии
  getSubIndustries: async () => {
    try {
      console.log('🏢 Fetching sub-industries from Libraries API...');
      const response = await gatewayApi.get('/libraries/sub-industries', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Sub-industries fetched successfully:', response.data);

      // Возвращаем данные в стандартном формате
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching sub-industries:', error);
      throw error;
    }
  },

  // Получить все inner clients
  getInnerClients: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/libraries/inner-clients', {
        params: { all: true, isCommon: 1, ...params }
      });
      return {
        success: true,
        data: response.data?.data ?? response.data ?? []
      };
    } catch (error) {
      console.error('❌ Error fetching inner clients:', error);
      throw error;
    }
  },

  // Получить все языки
  getLanguages: async () => {
    try {
      console.log('🌐 Fetching languages from Libraries API...');
      const response = await gatewayApi.get('/libraries/language', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Languages fetched successfully:', response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching languages:', error);
      throw error;
    }
  },


  // Получить все валюты
  getCurrencies: async () => {
    try {
      console.log('💰 Fetching currencies from Libraries API...');
      const response = await gatewayApi.get('/libraries/currencies', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Currencies fetched successfully:', response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching currencies:', error);
      throw error;
    }
  },

  // Получить все смены (shift)
  getShifts: async () => {
    try {
      console.log('🕐 Fetching shifts from Libraries API...');
      const response = await gatewayApi.get('/libraries/shifts', {
        params: { all: true, isCommon: 1 }
      });
      console.log('✅ Shifts fetched successfully:', response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching shifts:', error);
      throw error;
    }
  },

  // Получить все ставки (rate)
  getRates: async () => {
    try {
      console.log('📊 Fetching rates from Libraries API...');
      const response = await gatewayApi.get('/libraries/rates', {
        params: { all: true, isCommon: 1 }
      });
      console.log('✅ Rates fetched successfully:', response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching rates:', error);
      throw error;
    }
  },

  // Получить все уровни владения языками
  getLanguageLevels: async () => {
    try {
      console.log('📊 Fetching language levels from Libraries API...');
      const response = await gatewayApi.get('/libraries/levels', {
        params: { all: true, isCommon: 1, isShort: true }
      });
      console.log('✅ Language levels fetched successfully:', response.data);

      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching language levels:', error);
      throw error;
    }
  },

  // Найти инструмент по имени (используется для поиска tool_id Discord)
  getToolByName: async (name) => {
    try {
      const response = await gatewayApi.get('/libraries/tools', { params: { search: name, all: true, isShort: true } });
      const tools = response.data?.data || response.data || [];
      return Array.isArray(tools) ? tools : [];
    } catch (error) {
      console.error('Error fetching tool by name:', error);
      throw error;
    }
  },

  getStatuses: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/libraries/status', {
        params: { all: true, isCommon: 1, ...params },
      });
      return { success: true, data: response.data?.data || response.data || [] };
    } catch (error) {
      console.error('❌ Error fetching statuses via gateway:', error);
      return { success: false, data: [] };
    }
  },

};

// ==================== TALENT MEDIA SERVICE ====================
const talentMediaService = {
  // Загрузить медиа-файл
  uploadMedia: async (talentId, file, mediaType = 'audio', publicVisible = false, languageId = null) => {
    try {
      console.log(`📁 Uploading media file for talent ${talentId}...`);
      
      const formData = new FormData();
      formData.append('media_file', file);
      formData.append('media_type', mediaType);
      formData.append('public_visible', publicVisible.toString());
      if (languageId) {
        formData.append('language_id', languageId.toString());
      }

      const response = await mainApi.post(`/talents/${talentId}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ Media uploaded successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error uploading media:', error);
      throw error;
    }
  },

  // Создать медиа-запись (для URL)
  createMedia: async (mediaData) => {
    try {
      console.log(`📁 Creating media record for talent ${mediaData.talent_id}...`);
      console.log('📁 Media data:', mediaData);
      
      const response = await mainApi.post(`/talents/${mediaData.talent_id}/media`, mediaData);

      console.log('✅ Media record created successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error creating media record:', error);
      throw error;
    }
  },

  // Получить все медиа-файлы таланта
  getTalentMedia: async (talentId, options = {}) => {
    try {
      console.log(`📁 Fetching media files for talent ${talentId}...`);
      
      const params = new URLSearchParams();
      if (options.media_type) params.append('media_type', options.media_type);
      if (options.public_only) params.append('public_only', options.public_only);
      if (options.language_id) params.append('language_id', options.language_id);

      const response = await mainApi.get(`/talents/${talentId}/media?${params}`);
      
      console.log('✅ Media files fetched successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching media files:', error);
      throw error;
    }
  },

  // Получить конкретный медиа-файл
  getMediaById: async (talentId, mediaId) => {
    try {
      console.log(`📁 Fetching media file ${mediaId} for talent ${talentId}...`);
      
      const response = await mainApi.get(`/talents/${talentId}/media/${mediaId}`);
      
      console.log('✅ Media file fetched successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching media file:', error);
      throw error;
    }
  },

  // Обновить медиа-файл
  updateMedia: async (talentId, mediaId, updateData) => {
    try {
      console.log(`📁 Updating media file ${mediaId} for talent ${talentId}...`);
      
      const response = await mainApi.put(`/talents/${talentId}/media/${mediaId}`, updateData);
      
      console.log('✅ Media file updated successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error updating media file:', error);
      throw error;
    }
  },

  // Удалить медиа-файл
  deleteMedia: async (talentId, mediaId) => {
    try {
      console.log(`📁 Deleting media file ${mediaId} for talent ${talentId}...`);
      
      const response = await mainApi.delete(`/talents/${talentId}/media/${mediaId}`);
      
      console.log('✅ Media file deleted successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error deleting media file:', error);
      throw error;
    }
  },

  // Получить статистику медиа-файлов
  getMediaStats: async (talentId) => {
    try {
      console.log(`📁 Fetching media stats for talent ${talentId}...`);
      
      const response = await mainApi.get(`/talents/${talentId}/media/stats`);
      
      console.log('✅ Media stats fetched successfully:', response.data);
      return {
        success: true,
        data: response.data.data || response.data
      };
    } catch (error) {
      console.error('❌ Error fetching media stats:', error);
      throw error;
    }
  },

};

// ==================== BACKUP SERVICE ====================

const backupService = {
  // Получить список всех бэкапов
  listBackups: async () => {
    try {
      console.log('📦 Fetching backup list...');

      const response = await mainApi.get('/backup/list');

      console.log('✅ Backup list fetched successfully:', response.data);
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('❌ Error fetching backup list:', error);
      throw error;
    }
  },

  // Создать новый бэкап
  createBackup: async () => {
    try {
      console.log('📦 Creating new backup...');

      const response = await mainApi.post('/backup/create');

      console.log('✅ Backup created successfully:', response.data);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error creating backup:', error);
      throw error;
    }
  },

  // Восстановить базу данных из бэкапа
  restoreBackup: async (filename, confirmRestore = true) => {
    try {
      console.log(`📦 Restoring database from backup: ${filename}...`);

      const response = await mainApi.post(`/backup/restore/${filename}`, {
        confirmRestore
      });

      console.log('✅ Database restored successfully:', response.data);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error restoring backup:', error);
      throw error;
    }
  },

  // Скачать файл бэкапа
  downloadBackup: async (filename) => {
    try {
      console.log(`📦 Downloading backup: ${filename}...`);

      const response = await mainApi.get(`/backup/download/${filename}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Backup downloaded successfully:', filename);
      return { success: true };
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          error.response.data = json;
        } catch (_) {}
      }
      console.error('❌ Error downloading backup:', error);
      throw error;
    }
  },

  // Удалить бэкап
  deleteBackup: async (filename) => {
    try {
      console.log(`📦 Deleting backup: ${filename}...`);

      const response = await mainApi.delete(`/backup/${filename}`);

      console.log('✅ Backup deleted successfully:', response.data);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error deleting backup:', error);
      throw error;
    }
  },

  // Получить статус системы бэкапов
  getStatus: async () => {
    try {
      console.log('📦 Fetching backup system status...');

      const response = await mainApi.get('/backup/status');

      console.log('✅ Backup status fetched successfully:', response.data);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error fetching backup status:', error);
      throw error;
    }
  },

  // Получить историю бэкапов
  getHistory: async (limit = 50, offset = 0) => {
    try {
      console.log(`📦 Fetching backup history (limit: ${limit}, offset: ${offset})...`);

      const response = await mainApi.get('/backup/history', {
        params: { limit, offset }
      });

      console.log('✅ Backup history fetched successfully:', response.data);
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('❌ Error fetching backup history:', error);
      throw error;
    }
  },

  // Загрузить внешний бэкап
  uploadBackup: async (file, onProgress) => {
    try {
      console.log(`📤 Uploading external backup: ${file.name} (${file.size} bytes)...`);

      const formData = new FormData();
      formData.append('backupFile', file);

      const response = await mainApi.post('/backup/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => {
              const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
              onProgress(percent);
            }
          : undefined
      });

      console.log('✅ External backup uploaded successfully:', response.data);
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('❌ Error uploading backup:', error);
      throw error;
    }
  }
};

// ==================== TALENT TEMPLATES SERVICE ====================

const talentTemplateService = {
  getAll: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/talent-templates', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent templates:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await gatewayApi.get(`/talent-service/talent-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching talent template:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await mainApi.post('/talent-templates', data);
      return response.data;
    } catch (error) {
      console.error('Error creating talent template:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await mainApi.put(`/talent-templates/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating talent template:', error);
      throw error;
    }
  },

  remove: async (id) => {
    try {
      const response = await mainApi.delete(`/talent-templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent template:', error);
      throw error;
    }
  },

  getEvents: async (templateId, params = {}) => {
    try {
      const response = await mainApi.get(`/talent-templates/${templateId}/events`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent template events:', error);
      throw error;
    }
  },

  getEventStats: async (templateId, days = 30) => {
    try {
      const response = await mainApi.get(`/talent-templates/${templateId}/events/stats`, { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent template event stats:', error);
      throw error;
    }
  }
};

// ==================== TALENT POSTS SERVICE ====================

const talentPostService = {
  getAll: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/talent-posts', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent posts:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await mainApi.get(`/talent-posts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching talent post:', error);
      throw error;
    }
  },

  // Post with names/rates/talent data resolved by the gateway (detail view)
  getByIdEnriched: async (id) => {
    try {
      const response = await gatewayApi.get(`/talent-service/talent-posts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching enriched talent post:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await mainApi.post('/talent-posts', data);
      return response.data;
    } catch (error) {
      console.error('Error creating talent post:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await mainApi.put(`/talent-posts/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating talent post:', error);
      throw error;
    }
  },

  remove: async (id) => {
    try {
      const response = await mainApi.delete(`/talent-posts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent post:', error);
      throw error;
    }
  },

  getModels: async () => {
    try {
      const response = await mainApi.get('/talent-posts/models');
      return response.data;
    } catch (error) {
      console.error('Error fetching AI models:', error);
      throw error;
    }
  },

  generate: async (data = {}) => {
    try {
      const response = await gatewayApi.post('/talent-service/talent-posts/generate', data);
      return response.data;
    } catch (error) {
      console.error('Error generating post preview:', error);
      throw error;
    }
  },

  publish: async (id, data) => {
    try {
      const response = await mainApi.post(`/talent-posts/${id}/publish`, data);
      return response.data;
    } catch (error) {
      console.error('Error publishing talent post:', error);
      throw error;
    }
  },

  unpublish: async (id) => {
    try {
      const response = await mainApi.post(`/talent-posts/${id}/unpublish`);
      return response.data;
    } catch (error) {
      console.error('Error unpublishing talent post:', error);
      throw error;
    }
  },

  // Post name and a free slug — the formulas live on the backend
  generateIdentifiers: async (params = {}) => {
    try {
      const response = await mainApi.get('/talent-posts/generate-identifiers', { params });
      return response.data;
    } catch (error) {
      console.error('Error generating talent post identifiers:', error);
      throw error;
    }
  },

  checkSlug: async (slug, excludeId = null) => {
    try {
      const params = { slug };
      if (excludeId) params.exclude_id = excludeId;
      const response = await mainApi.get('/talent-posts/check-slug', { params });
      return response.data;
    } catch (error) {
      console.error('Error checking talent post slug:', error);
      throw error;
    }
  },

  getTerms: async ({ postId = null, talentId = null } = {}) => {
    try {
      if (postId) {
        const response = await gatewayApi.get(`/talent-service/talent-posts/${postId}/terms`);
        return response.data;
      }
      const response = await gatewayApi.get('/talent-service/talent-posts/terms', { params: { talent_id: talentId } });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent post terms:', error);
      throw error;
    }
  },

  saveTerms: async (postId, data, isNew = false) => {
    try {
      const response = isNew
        ? await mainApi.post(`/talent-posts/${postId}/terms`, data)
        : await mainApi.put(`/talent-posts/${postId}/terms`, data);
      return response.data;
    } catch (error) {
      console.error('Error saving talent post terms:', error);
      throw error;
    }
  },

  savePrices: async (postId, data, isNew = false) => {
    try {
      const response = isNew
        ? await mainApi.post(`/talent-posts/${postId}/prices`, data)
        : await mainApi.put(`/talent-posts/${postId}/prices`, data);
      return response.data;
    } catch (error) {
      console.error('Error saving talent post prices:', error);
      throw error;
    }
  },

  deleteTermRecord: async (postId, termType, termId) => {
    try {
      const response = await mainApi.delete(`/talent-posts/${postId}/terms/${termType}/${termId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent post term record:', error);
      throw error;
    }
  },

  clearTerms: async (postId, params = {}) => {
    try {
      const response = await mainApi.delete(`/talent-posts/${postId}/terms`, { params });
      return response.data;
    } catch (error) {
      console.error('Error clearing talent post terms:', error);
      throw error;
    }
  },

  deletePrices: async (postId) => {
    try {
      const response = await mainApi.delete(`/talent-posts/${postId}/prices`);
      return response.data;
    } catch (error) {
      console.error('Error deleting talent post prices:', error);
      throw error;
    }
  },

  getEvents: async (postId, params = {}) => {
    try {
      const response = await gatewayApi.get(`/talent-service/talent-posts/${postId}/events`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent post events:', error);
      throw error;
    }
  },

  getEventStats: async (postId, days = 30) => {
    try {
      const response = await mainApi.get(`/talent-posts/${postId}/events/stats`, { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching talent post event stats:', error);
      throw error;
    }
  },

  // Публичный фид постов (без авторизации, через X-API-Token)
  getFeed: async (params = {}) => {
    try {
      const response = await publicApi.get('/talent-service/talent-posts/feed', { params });
      const raw = response.data || {};
      const inner = raw.data;
      const items = Array.isArray(inner) ? inner : (Array.isArray(inner?.data) ? inner.data : []);
      const count = raw.count ?? inner?.count ?? items.length;
      const totalPages = raw.totalPages ?? inner?.totalPages ?? null;
      return { data: items, count, totalPages };
    } catch (error) {
      console.error('Error fetching posts feed:', error);
      throw error;
    }
  },

  getFeedFilters: async (params = {}) => {
    try {
      const response = await publicApi.get('/talent-service/talent-posts/feed/filters', { params });
      const payload = response.data?.data || response.data || {};
      return payload?.data || payload || {};
    } catch (error) {
      console.error('Error fetching posts feed filters:', error);
      return {};
    }
  },

  // Департаменты и профессии фида с ценами из Libraries (публичный прайс-каталог)
  getFeedProfessions: async (params = {}) => {
    try {
      const response = await publicApi.get('/talent-service/talent-posts/feed/professions', { params });
      const raw = response.data || {};
      const inner = raw.data;
      if (Array.isArray(inner)) return inner;
      if (Array.isArray(inner?.data)) return inner.data;
      return Array.isArray(raw) ? raw : [];
    } catch (error) {
      console.error('Error fetching feed professions with prices:', error);
      throw error;
    }
  },

  getFeedBySlug: async (slug, params = {}) => {
    try {
      const response = await publicApi.get(`/talent-service/talent-posts/feed/${slug}`, { params });
      const raw = response.data || {};
      if (raw?.data?.data) return raw.data.data;
      if (raw?.data) return raw.data;
      return raw;
    } catch (error) {
      console.error('Error fetching post by slug:', error);
      throw error;
    }
  },

  getSimilarPosts: async (slug, params = {}) => {
    try {
      const response = await publicApi.get(`/talent-service/talent-posts/feed/${slug}/similar`, { params });
      const raw = response.data || {};
      const inner = raw.data;
      const items = Array.isArray(inner) ? inner : (Array.isArray(inner?.data) ? inner.data : []);
      return { data: items, count: items.length };
    } catch (error) {
      console.error('Error fetching similar posts:', error);
      return { data: [], count: 0 };
    }
  }
};

// ==================== PROMPT TEMPLATES SERVICE ====================

const promptTemplateService = {
  getAll: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/prompts', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching prompt templates:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await gatewayApi.get(`/talent-service/prompts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching prompt template:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await mainApi.post('/prompts', data);
      return response.data;
    } catch (error) {
      console.error('Error creating prompt template:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await mainApi.put(`/prompts/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating prompt template:', error);
      throw error;
    }
  },

  remove: async (id) => {
    try {
      const response = await mainApi.delete(`/prompts/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting prompt template:', error);
      throw error;
    }
  },

  match: async (params = {}) => {
    try {
      const response = await mainApi.get('/prompts/match', { params });
      return response.data;
    } catch (error) {
      console.error('Error matching prompt templates:', error);
      throw error;
    }
  },

  getEvents: async (promptId, params = {}) => {
    try {
      const response = await mainApi.get(`/prompts/${promptId}/events`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching prompt template events:', error);
      throw error;
    }
  },

  getEventStats: async (promptId, days = 30) => {
    try {
      const response = await mainApi.get(`/prompts/${promptId}/events/stats`, { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching prompt template event stats:', error);
      throw error;
    }
  }
};

// ==================== GLOBAL EVENTS METHODS ====================

const notificationConfigService = {
  getAll: async () => {
    try {
      const response = await gatewayApi.get('/talent-service/notification-configs');
      return response.data;
    } catch (error) {
      console.error('Error fetching notification configs:', error);
      throw error;
    }
  },
  create: async (data) => {
    try {
      const response = await mainApi.post('/notification-configs', data);
      return response.data;
    } catch (error) {
      console.error('Error creating notification config:', error);
      throw error;
    }
  },
  update: async (id, data) => {
    try {
      const response = await mainApi.put(`/notification-configs/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating notification config:', error);
      throw error;
    }
  },
  remove: async (id) => {
    try {
      const response = await mainApi.delete(`/notification-configs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification config:', error);
      throw error;
    }
  },
};

const eventsService = {
  getGroupedEvents: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/events/grouped', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching grouped events:', error);
      throw error;
    }
  },

  getEntityEvents: async (entityType, entityId, params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/events', {
        params: { 'eventable_types[]': entityType, eventable_id: entityId, limit: 100, ...params },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching entity events:', error);
      throw error;
    }
  },

  getGlobalEvents: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/events', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching global events:', error);
      throw error;
    }
  },

  cancelEvent: async (id, reason = null) => {
    try {
      const response = await gatewayApi.patch(`/talent-service/events/${id}/cancel`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error cancelling event:', error);
      throw error;
    }
  },

  uncancelEvent: async (id) => {
    try {
      const response = await gatewayApi.patch(`/talent-service/events/${id}/uncancel`);
      return response.data;
    } catch (error) {
      console.error('Error uncancelling event:', error);
      throw error;
    }
  },
};

const playbookService = {
  list: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/playbooks', { params });
      return response.data;
    } catch (error) { console.error('Error fetching playbooks:', error); throw error; }
  },
  get: async (id) => {
    try {
      const response = await gatewayApi.get(`/talent-service/playbooks/${id}`);
      return response.data;
    } catch (error) { console.error('Error fetching playbook:', error); throw error; }
  },
  create: async (data) => {
    try {
      const response = await gatewayApi.post('/talent-service/playbooks', data);
      return response.data;
    } catch (error) { console.error('Error creating playbook:', error); throw error; }
  },
  update: async (id, data) => {
    try {
      const response = await gatewayApi.put(`/talent-service/playbooks/${id}`, data);
      return response.data;
    } catch (error) { console.error('Error updating playbook:', error); throw error; }
  },
  deactivate: async (id) => {
    try {
      const response = await gatewayApi.delete(`/talent-service/playbooks/${id}`);
      return response.data;
    } catch (error) { console.error('Error deactivating playbook:', error); throw error; }
  },
  addStep: async (playbookId, data) => {
    try {
      const response = await gatewayApi.post(`/talent-service/playbooks/${playbookId}/steps`, data);
      return response.data;
    } catch (error) { console.error('Error adding step:', error); throw error; }
  },
  updateStep: async (id, data) => {
    try {
      const response = await gatewayApi.put(`/talent-service/playbook-steps/${id}`, data);
      return response.data;
    } catch (error) { console.error('Error updating step:', error); throw error; }
  },
  deleteStep: async (id) => {
    try {
      const response = await gatewayApi.delete(`/talent-service/playbook-steps/${id}`);
      return response.data;
    } catch (error) { console.error('Error deleting step:', error); throw error; }
  },
  reorderSteps: async (playbookId, orderedStepIds) => {
    try {
      const response = await gatewayApi.put(`/talent-service/playbooks/${playbookId}/steps/reorder`, { ordered_step_ids: orderedStepIds });
      return response.data;
    } catch (error) { console.error('Error reordering steps:', error); throw error; }
  },

  // Instances
  listForTalent: async (talentId, params = {}) => {
    try {
      const response = await gatewayApi.get(`/talent-service/talents/${talentId}/playbook-instances`, { params });
      return response.data;
    } catch (error) { console.error('Error fetching talent playbook instances:', error); throw error; }
  },
  listAllInstances: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/playbook-instances', { params });
      return response.data;
    } catch (error) { console.error('Error fetching playbook instances:', error); throw error; }
  },
  dashboardStats: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/playbook-instances/stats', { params });
      return response.data;
    } catch (error) { console.error('Error fetching dashboard stats:', error); throw error; }
  },
  getCalendarData: async (params = {}) => {
    try {
      const response = await gatewayApi.get('/talent-service/playbook-instances/calendar', { params });
      return response.data;
    } catch (error) { console.error('Error fetching playbook calendar:', error); throw error; }
  },
  start: async (talentId, body) => {
    try {
      const response = await gatewayApi.post(`/talent-service/talents/${talentId}/playbook-instances`, body);
      return response.data;
    } catch (error) { console.error('Error starting playbook instance:', error); throw error; }
  },
  cancelInstance: async (id, reason) => {
    try {
      const response = await gatewayApi.patch(`/talent-service/playbook-instances/${id}/cancel`, { reason });
      return response.data;
    } catch (error) { console.error('Error cancelling instance:', error); throw error; }
  },
  extendInstance: async (id, endsAt) => {
    try {
      const response = await gatewayApi.patch(`/talent-service/playbook-instances/${id}/extend`, { ends_at: endsAt });
      return response.data;
    } catch (error) { console.error('Error extending instance:', error); throw error; }
  },
  completeStep: async (stepInstanceId) => {
    try {
      const response = await gatewayApi.patch(`/talent-service/playbook-step-instances/${stepInstanceId}/complete`);
      return response.data;
    } catch (error) { console.error('Error completing step:', error); throw error; }
  },
  uncompleteStep: async (stepInstanceId) => {
    try {
      const response = await gatewayApi.patch(`/talent-service/playbook-step-instances/${stepInstanceId}/uncomplete`);
      return response.data;
    } catch (error) { console.error('Error uncompleting step:', error); throw error; }
  },
};

// ==================== JOBS SERVICE (jobs-service via gateway) ====================

const jobsService = {
  // Список job-постов в коротком виде {id, name} — для дропдаунов (Talent / JobApplication форма)
  // По умолчанию all=true (без пагинации) + short=true (только id+name).
  listPosts: async ({ search, all = true, short = true, page, per_page } = {}) => {
    try {
      const params = { all, short, isCommon: 1 };
      if (search) params.search = search;
      if (page) params.page = page;
      if (per_page) params.per_page = per_page;
      const response = await gatewayApi.get('/job-service/job-posts', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error fetching job posts:', error);
      throw error;
    }
  },
  // Получить один job-пост по id (используется в edit-mode для отображения уже выбранного)
  getPostById: async (id) => {
    try {
      const response = await gatewayApi.get(`/job-service/job-posts/${id}`, {
        params: { isCommon: 1 }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`❌ Error fetching job post ${id}:`, error);
      throw error;
    }
  },
};

// ==================== EXPORT UNIFIED SERVICE ====================

const apiService = {
  // Talent methods
  talents: talentService,
  
  // Talent Media methods
  talentMedia: talentMediaService,
  
  // Status methods
  statuses: statusService,

  // Stage methods
  stages: stageService,

  // Job Application methods
  jobApplications: jobApplicationService,
  
  // Notes methods
  notes: notesService,
  
  // User methods
  users: userService,
  
  // Account methods
  accounts: accountService,
  
  // Course methods
  courses: courseService,
  
  // Libraries methods
  libraries: librariesService,

  // Backup methods
  backup: backupService,

  // Talent Templates methods
  talentTemplates: talentTemplateService,

  // Talent Posts methods
  talentPosts: talentPostService,

  // Prompt Templates methods
  promptTemplates: promptTemplateService,

  // Global Events methods
  events: eventsService,

  // Notification Configs methods
  notificationConfigs: notificationConfigService,

  // Playbooks (lifecycle stages)
  playbooks: playbookService,

  // Jobs (jobs-service)
  jobs: jobsService,

  // Tasks (task-manager-service, via api-gateway aggregation)
  tasks: {
    async list(params = {}) {
      const response = await gatewayApi.get('/task-manager-service/tasks', { params });
      return response.data;
    },
    async listStatuses() {
      const response = await gatewayApi.get('/task-manager-service/task-statuses');
      return response.data;
    },
  },
};

// Экспортируем отдельные сервисы для деструктуризации
export const { talents, talentMedia, statuses, stages, jobApplications, notes, users, accounts, courses, libraries, backup, talentTemplates, talentPosts, promptTemplates, events, notificationConfigs, playbooks, jobs, tasks } = apiService;

// Экспортируем axios instances для использования в других сервисах
export { mainApi, gatewayApi, publicApi };

export const getUsedCityIds = async () => {
  const response = await mainApi.get('/talents/cities/used');
  return response.data;
};

